import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Activity, ActivityType, VibrationMode } from '@/types/activity';
import type { DownloadProgress, DownloadStructure } from '@/types/download';
import { useToast } from '@/contexts/toastContextBase';
import { downloadBlob } from '@/utils/download';
import { useFilteredActivitySelection } from '@/hooks/useFilteredActivitySelection';
import { isActivitySelectable } from '@/types/activity';
import { applyActivityFilters } from '@/utils/activityFilters';
import {
  getStoredDownloadStructure,
  setStoredDownloadStructure,
} from '@/utils/downloadStructure';
import {
  dedupeVibrationModes,
  getPersistedVibrationModes,
  persistDiscoveredVibrationModes,
} from '@/utils/vibrationModes';
import {
  formatPatientDownloadSuccessMessage,
  getPatientDownloadModeConfig,
  getSelectedActivityIds,
  type DownloadMode,
} from './downloadWorkflow';

interface UsePatientDownloadControllerOptions {
  mode: DownloadMode;
  activities: Activity[];
}

export function usePatientDownloadController({
  mode,
  activities,
}: UsePatientDownloadControllerOptions) {
  const { showToast } = useToast();
  const config = useMemo(() => getPatientDownloadModeConfig(mode), [mode]);
  const isSelectable = useCallback(
    (activity: Activity) => mode !== 'processed' || isActivitySelectable(activity),
    [mode]
  );

  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [vibrationModes, setVibrationModes] = useState<VibrationMode[]>([]);
  const [dateRange, setDateRange] = useState<{ from: string | null; to: string | null }>({
    from: null,
    to: null,
  });
  const [downloadStructure, setDownloadStructureState] = useState<DownloadStructure>(
    () => getStoredDownloadStructure()
  );
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress>({
    status: 'idle',
    current: 0,
    total: 0,
  });

  const availableVibrationModes = useMemo(
    () =>
      dedupeVibrationModes([
        ...getPersistedVibrationModes(),
        ...activities.map((activity) => activity.vibrationMode),
        ...vibrationModes,
      ]),
    [activities, vibrationModes]
  );

  const currentlyAvailableVibrationModes = useMemo(() => {
    const scopedActivities = applyActivityFilters(activities, {
      types: activityTypes,
      startDate: dateRange.from ?? undefined,
      endDate: dateRange.to ?? undefined,
    });

    return new Set(scopedActivities.map((activity) => activity.vibrationMode));
  }, [activities, activityTypes, dateRange.from, dateRange.to]);

  useEffect(() => {
    persistDiscoveredVibrationModes(activities.map((activity) => activity.vibrationMode));
  }, [activities]);

  const {
    filteredActivities,
    selectedActivities,
    selectedActivityIds,
    toggleActivitySelection,
    selectAllActivities,
    deselectAllActivities,
    resetSelection,
  } = useFilteredActivitySelection({
    activities,
    filters: {
      types: activityTypes,
      vibrationModes,
      startDate: dateRange.from ?? undefined,
      endDate: dateRange.to ?? undefined,
    },
    isSelectable,
  });

  const hasFilters =
    activityTypes.length > 0 ||
    vibrationModes.length > 0 ||
    dateRange.from !== null ||
    dateRange.to !== null;

  const handleDateRangeChange = useCallback((from: string | null, to: string | null) => {
    setDateRange({ from, to });
  }, []);

  const clearFilters = useCallback(() => {
    setActivityTypes([]);
    setVibrationModes([]);
    setDateRange({ from: null, to: null });
    resetSelection();
  }, [resetSelection]);

  const setDownloadStructure = useCallback((value: DownloadStructure) => {
    setDownloadStructureState(value);
    setStoredDownloadStructure(value);
  }, []);

  const handleDownload = useCallback(async () => {
    if (selectedActivities.length === 0) {
      showToast('warning', 'No activities selected');
      return;
    }

    setDownloadProgress({ status: 'fetching', current: 0, total: 1 });

    try {
      const activityIds = getSelectedActivityIds(selectedActivities);
      const zipBlob = await config.downloadAction(activityIds, downloadStructure);

      setDownloadProgress({ status: 'packaging', current: 1, total: 1 });
      downloadBlob(zipBlob, `${config.filenamePrefix}-${new Date().toISOString().split('T')[0]}.zip`);

      setDownloadProgress({ status: 'complete', current: 1, total: 1 });
      showToast(
        'success',
        formatPatientDownloadSuccessMessage(selectedActivities.length, config.resultLabel)
      );

      setTimeout(() => {
        setDownloadProgress({ status: 'idle', current: 0, total: 0 });
      }, 2000);
    } catch (error) {
      setDownloadProgress({
        status: 'error',
        current: 0,
        total: 0,
        message: error instanceof Error ? error.message : 'Download failed',
      });
      showToast('error', config.downloadErrorMessage);
    }
  }, [selectedActivities, showToast, config, downloadStructure]);

  return {
    config,
    activityTypes,
    vibrationModes,
    dateRange,
    downloadStructure,
    availableVibrationModes,
    currentlyAvailableVibrationModes,
    filteredActivities,
    selectedActivities,
    selectedActivityIds,
    downloadProgress,
    hasFilters,
    setActivityTypes,
    setVibrationModes,
    setDownloadStructure,
    handleDateRangeChange,
    clearFilters,
    toggleActivitySelection,
    selectAllActivities,
    deselectAllActivities,
    handleDownload,
  };
}
